import React, { useEffect, useRef } from "react";
import ImageSwitch from "./ImageSwitch";
import classNames from "classnames";
import { RichText, Text } from "@sitecore-jss/sitecore-jss-react";
import { getOverallSettingsClasses, loadScrollMagic, mobileIs, componentId } from "../../utils/siteUtils";
import { BackgroundImage } from "../../elements";
import "./style.scss";
import gsap from 'gsap';

const ImmersiveSlider = props => {
  const { rendering, fields, params } = props;
  const { title, pc_text, m_text, list } = fields;
  const { uid } = rendering;
  const isMobile = mobileIs()
  const rootEl = useRef()
  const emptyEl = useRef()
  const lastSlideEl = useRef();
  const titleEl = useRef()
  const subTitleEl = useRef();
  const slideBodyEl = useRef();
  const autoScrollTime = useRef(0);
  const scrollPosition = useRef(0);
  const scrollDirection = useRef(null);
  const easingTimer = useRef(0);
  const isUserScrollTimer = useRef(0);
  const isUserScroll = useRef(true);

  useEffect(() => {
    let ScrollMagic, controller, sceneTitle, tl;
    const isMac = navigator.userAgent.includes('Mac');
    const scenes = [];
    ScrollMagic = loadScrollMagic();
    controller = new ScrollMagic.Controller();

    const scrolling = (direction) => {
      if (direction !== scrollDirection.current) {
        return false;
      }

      autoScrollTime.current = autoScrollTime.current + 8; // ms
      if ( autoScrollTime.current > 30 * 8) {
        return false;
      }
      window.scrollTo({
        top: window.scrollY + 1 * direction,
        behavior: 'instant'
      });

      setTimeout(() => {
        scrolling(direction);
      }, 8, direction);
    }
    const handleScroll = (e) => {
      if (isMobile || 
        rootEl.current.getBoundingClientRect().top > window.innerHeight || 
        emptyEl.current.getBoundingClientRect().top < 80 ||
        emptyEl.current.getBoundingClientRect().bottom < window.innerHeight) {
        return false;
      }
      if (!isUserScroll.current) {
        return false;
      }
      const direction = window.scrollY - scrollPosition.current > 0 ? 1 : -1;
      scrollDirection.current = direction;
      clearTimeout(easingTimer.current);
      clearTimeout(isUserScrollTimer.current);

      if (scrollDirection.current !== null && scrollDirection.current !== direction) {
        // Direction changed
        isUserScroll.current = true;
        easingTimer.current = 0;
        return false;
      }

      if (Math.abs(window.scrollY - scrollPosition.current) < 10) {
        easingTimer.current = setTimeout(() => {
          isUserScroll.current = false;
          easingTimer.current = 0;
          scrolling(direction);
          isUserScrollTimer.current = setTimeout(() => {
            autoScrollTime.current = 0;
            isUserScroll.current = true;
          }, isMac ? 300 : 300);
        }, 10);
      }
      scrollPosition.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);

    tl = gsap.timeline();

    tl.add(
      gsap.to(
        titleEl.current, {
        y: 0,
        opacity: 1,
        offset: 50
      }
      )
    )
    .add(
      gsap.to(
        subTitleEl.current, {
        y: 0,
        opacity: 1,
      }
      )
    );

    sceneTitle = new ScrollMagic.Scene({
      triggerElement: titleEl.current,
      triggerHook: 0.9,
      duration: 0
    })
    .setTween(tl)
    .addTo(controller);

    scenes.push(sceneTitle)

    const sceneSlider = new ScrollMagic.Scene({
      triggerElement: emptyEl.current,
      triggerHook: 1,
      reverse: true,
      duration: 10,
      offset: 20
    }).on('leave', function(event) {
      if (event.scrollDirection === 'FORWARD') {
        const slides = rootEl.current.querySelectorAll('.immersive-slider__image-content');
        setTimeout(() => {
          slides.forEach(element => {
            element.classList.remove('pinned--element');
          });
        }, 0);
      } else {
        const slides = rootEl.current.querySelectorAll('.immersive-slider__image-content');
        slides.forEach((element, index) => {
          if (!(isMobile && index > 1)) {
            element.classList.add('pinned--element');
          }
        });
      }

    }).addTo(controller);

    scenes.push(sceneSlider);
  
    if (rootEl.current) {
      const slides = rootEl.current.querySelectorAll('.immersive-slider__image-content');

      // Slides 
      slides.forEach((item, index) => {
        const title = item.querySelector('.immersive-slider__text-content');
        const bg = item.querySelector('.immersive-slider__image-content__image');
        
        if (isMobile) {
          if(index < 2) {
            gsap.set(title, {
              y: 60,
              opacity: 0,
              ease: "power2.out"
            })
          }
        } else {
            gsap.set(title, {
              y: 60,
              opacity: 0,
              ease: "power2.out"
            })
        }


        // First Slide
        if (index === 0) {
          const tl = gsap.timeline();
          const imgScale = isMobile ? 0.91 : 0.8;
          gsap.set(item, { scale: imgScale, borderRadius: 15});

          const tween = tl.to(item, {
            // scale: 1,
            borderRadius: 0,
            ease: "power1.out"
          });

          const tlSlideTitle = gsap.timeline();
          const tweenSlideTitle = tlSlideTitle.to(bg, {
              boxShadow: "inset 0px 0px 30vh 30vh rgba(0,0,0,0.44)",
              // duration: 0.8,
              // delay: 0.8
            })
            .to(title, {
              opacity: 1,
              translateY: 0,
              ease: "power2.out",
              delay: 0,
            })


          const scene = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            reverse: true,
            duration: window.innerHeight
          })
            .setPin(item, { pushFollowers: true })
            .addTo(controller)
            .on('end', function (event) {
              item.classList.add('pinned--element');
            })
            .on('start', function (event) {
              item.classList.remove('pinned--element')
            });

          const sceneScale = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0.5,
            reverse: true,
            duration: window.innerHeight / 2
          })
            .setTween(tween)
            .addTo(controller);

          const sceneSlideTitle = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            reverse: true,
            duration: 0
          })
          .setTween(tweenSlideTitle)
          .addTo(controller);

          // Parallax 
          const scenePara = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 1,
            duration: window.innerHeight - 80
          })
          .on('progress', function(event) {            
            const process = 1 - event.progress.toFixed(4);
            const bg = item.querySelector('.immersive-slider__image-content__image') || item.querySelector('.image-switch');
            const pos = "translate3d(0, " + Math.round(process * -0.3 * window.innerHeight) + "px, 0)";

            const scale = imgScale + (1 - imgScale) * event.progress.toFixed(4);
            const style = item.getAttribute('style');
            const regex = /(scale\()(.*?)(\))/;
            const newStyle= style.replace(regex, `$1${scale.toFixed(3)}$3`)
            setTimeout(() => {
              item.setAttribute('style', newStyle);
            }, 0);
            
            setTimeout(() => {
              bg.style.transform = pos;
            }, 0);
          })
          .addTo(controller);
          scenes.push(scenePara);

          scenes.push(sceneSlideTitle);
          scenes.push(sceneScale);
          scenes.push(scene);
        } else if (!(isMobile && index >= 2) ) {
          // Other Slides
          const tl = new gsap.timeline();
          const imgSwitch = item.querySelector('.image-switch .swiper-controls');
          if (imgSwitch) {
            gsap.set(imgSwitch, {
              opacity: 0,
              y: '50vh',
              ease: "power2.out"
            })
          }

          const tween = tl
            .to(bg, {
              boxShadow: "inset 0px 0px 30vh 30vh rgba(0,0,0,0.44)",
              // duration: 0,
              delay: 0
            })
            .to(title, {
              opacity: 1,
              y: 0,
              ease: "power2.out",
              delay: 0,
            }, "<")
            .to(imgSwitch, {
              opacity: 1,
              y: 0,
              ease: "power2.out"
            })


          const scene = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            reverse: true,
            duration: window.innerHeight
          })
            .setPin(item, { pushFollowers: true })
            .addTo(controller)
            .on('end', function (event) {
              item.classList.add('pinned--element')
            })
            .on('start', function (event) {
              item.classList.remove('pinned--element')
            });

          const sceneSlideTitle = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            reverse: true,
            duration: 0
          })
          .setTween(tween)
          .addTo(controller);

          // Parallax 
          const scenePara = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 1,
            duration: window.innerHeight
          })
          .on('progress', function(event) {
            const process = 1 - event.progress.toFixed(4);
            const bg = item.querySelector('.immersive-slider__image-content__image') || item.querySelector('.image-switch');
            if (bg) {
              const pos = "translate3d(0, " + Math.round(process * -0.3 * window.innerHeight) + "px, 0)";

              setTimeout(() => {
                bg.style.transform = pos;
              }, 0);
            }
          })
          .addTo(controller);
          scenes.push(scenePara);

          scenes.push(sceneSlideTitle);
          scenes.push(scene);
        } else {
          lastSlideEl.current = item;

          const scene = new ScrollMagic.Scene({
            triggerElement: item,
            triggerHook: 0,
            reverse: false,
            duration: 1
          })
          .addTo(controller)
          .on('leave', function (event) {
            const slides = rootEl.current.querySelectorAll('.pinned--element');
            slides.forEach(element => {
              element.classList.remove('pinned--element');
            });
          });

          scenes.push(scene);
        }
      });
    }

    return () => {
      scenes.forEach(scene => scene.destroy(true));
      window.removeEventListener('scroll', handleScroll)
      controller.destroy(true);
    };
  }, [])

  const rootClasses = classNames("immersive-slider", getOverallSettingsClasses(fields));
  const rootId = componentId(rendering);
  return (
    <div className={rootClasses} datasourceid={rendering.dataSource} ref={rootEl} id={rootId}>
      <div className="q-container">
        <div className="immersive-slider__title">
          <div ref={titleEl} className={`immersive-slider__title__main ${params?.UppercaseTitle ? 'text-uppercase' : ''}`}>
            <Text field={title}></Text>
          </div>
          <div ref={subTitleEl} className="immersive-slider__title__sub">
            <RichText field={pc_text}></RichText>
          </div>
        </div>
      </div>


      <div className="immersive-slider__body" ref={slideBodyEl}>
        {
          list.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <div className={`immersive-slider__image-content ${index === list.length - 1 ? 'last-slide' : ''}`} key={index} id={`${uid}-card-${index}`}>
                  {
                    item?.fields?.list?.length ? (
                      // Contains Swiper Slider
                      <ImageSwitch items={item?.fields?.list} />
                    ) : (
                      <>
                        {!isMobile && <BackgroundImage {...item?.fields?.pc_image} className="immersive-slider__image-content__image" />}
                        {isMobile && <BackgroundImage {...item?.fields?.m_image} className="immersive-slider__image-content__image" />}
                      </>
                    )
                  }


                  <div className={`immersive-slider__text-content ${item?.fields?.alignment?.displayName}`}>
                    <div className="q-container">
                      {title && <Text tag="div" className={`immersive-slider__text-content__title ${params?.UppercaseTitle ? 'text-uppercase' : ''}`} field={item?.fields?.title} />}
                      {!isMobile && pc_text && <RichText tag="div" className="immersive-slider__text-content__text" field={item?.fields?.pc_text} />}
                      {isMobile && m_text && <RichText tag="div" className="immersive-slider__text-content__text" field={item?.fields?.m_text} />}
                    </div>
                  </div>
                </div>

                {
                  (( isMobile && index === 1) || (!isMobile && index === list.length - 1)) && <div className="empty" ref={emptyEl}></div>
                }
              </React.Fragment>
            );
          })
        }
      </div>


      
    </div>
  );
};

export default ImmersiveSlider;